import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { VitrinService } from "@/services/vitrin-service";
import type { VitrinCategory } from "@/models/home/VitrinCategory";
import SearchClient from "@/features/search/components/search-client";
import SearchSkeleton from "@/features/search/components/search-skeleton";
import { trackCategoryView } from "@/features/analytics";
import TopBar from "@/components/top-bar";
import { useIsDesktop } from "@/hooks/use-is-desktop";

// Categories arrive as a two-level tree, and either level can be the one being
// browsed — so the title has to look one deep as well.
function findCategoryName(
  categories: VitrinCategory[],
  id: number,
): string | undefined {
  for (const category of categories) {
    if (category.id === id) return category.name;
    const child = category.children?.find((item) => item.id === id);
    if (child) return child.name;
  }
  return undefined;
}

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const categoryIdNum = parseInt(categoryId ?? "", 10);
  // The phone leads with the sticky search field — that bar *is* the header
  // there. Desktop has the room to name the category above it instead.
  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (Number.isNaN(categoryIdNum)) return;
    trackCategoryView({ category_id: categoryIdNum });
  }, [categoryIdNum]);

  // Same categories source as the search page so the filter UI is identical.
  const { data, isPending } = useQuery({
    queryKey: ["search-categories"],
    queryFn: () => VitrinService.getCategories(),
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });
  const categories = data?.data ?? [];
  const categoryName = Number.isNaN(categoryIdNum)
    ? undefined
    : findCategoryName(categories, categoryIdNum);

  // `noBody` keeps the phone layout untouched while still wiring the hardware
  // back button — the bar only grows a body once there's a desktop to fill.
  const topBar = (
    <TopBar
      noBody={!isDesktop}
      title={categoryName || "دسته بندی"}
      className="lg:h-16 lg:px-6"
    />
  );

  if (isPending) {
    return (
      <div className="min-h-svh bg-secondary-background w-full pb-20 no-select lg:pb-8">
        {topBar}
        <SearchSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-svh bg-secondary-background w-full pb-20 no-select lg:pb-8">
      {topBar}
      <SearchClient
        // Remount when the category changes so its selection re-seeds.
        key={categoryIdNum}
        categories={categories}
        initialCategoryId={Number.isNaN(categoryIdNum) ? undefined : categoryIdNum}
      />
    </div>
  );
}
